// Fatia S3a: início da empresa (resumo em uma frase, KPIs, gráfico, ritmo do orçamento, sugestões e campanhas).
import { Link } from 'react-router-dom'
import { ArrowRight, MessageCircle, Receipt, TrendingUp, Wallet } from 'lucide-react'
import { num } from '@impulso/shared'
import { useCan, useCompany } from '../../lib/company'
import { useSession } from '../../lib/session'
import { Delta, Empty, ErrorState, Loading } from '../../ui'
import { SpendContactsChart } from '../../ui/charts'
import TopSuggestions from '../../features/recommendations/TopSuggestions'
import { useDashboard } from '../../features/insights/api'
import { greeting, Kpi, money, money2, NoConnections, PacingCard, roasText, SyncingBanner, toChartData, vsLabel } from '../../features/insights/components'
import { CampaignSummaryTable } from '../../features/campaigns/CampaignList'

export default function Dashboard() {
  const company = useCompany()
  const { me } = useSession()
  const canConnect = useCan('company.manage_connections')
  const q = useDashboard(company.id, 30)
  const firstName = me?.user.name.split(' ')[0] ?? ''

  const head = (
    <div className="page-head" style={{ marginBottom: 0 }}>
      <div>
        <h1>{greeting()}{firstName ? `, ${firstName}` : ''} 👋</h1>
        <p className="sub">Veja como os anúncios de {company.name} foram nos últimos 30 dias.</p>
      </div>
    </div>
  )

  if (q.isLoading) return <div className="stack" style={{ gap: 20 }}>{head}<div className="card"><Loading lines={4} /></div></div>
  if (q.isError || !q.data) return <div className="stack" style={{ gap: 20 }}>{head}<ErrorState error={q.error} onRetry={() => q.refetch()} /></div>

  const d = q.data
  if (!d.hasConnections) {
    return (
      <div className="stack" style={{ gap: 20 }}>
        {head}
        <NoConnections companyId={company.id} canConnect={canConnect} />
      </div>
    )
  }

  const k = d.kpis
  const vs = vsLabel(30)
  return (
    <div className="stack" style={{ gap: 20 }}>
      {head}
      {d.syncing && <SyncingBanner />}

      <div className="card hero">
        <div className="row wrap" style={{ gap: 24, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="small muted">RESUMO EM UMA FRASE</div>
            <p style={{ fontSize: 19, lineHeight: 1.45, marginTop: 8, fontWeight: 500 }}>{d.headline}</p>
          </div>
          <div className="stack" style={{ gap: 8, minWidth: 200 }}>
            <Link to={`/app/${company.id}/sugestoes`} className="btn white">Ver sugestões <ArrowRight size={16} /></Link>
            <Link to={`/app/${company.id}/resultados`} className="btn on-hero">Relatório completo</Link>
          </div>
        </div>
      </div>

      <div className="grid g-4 g-kpi">
        <Kpi icon={Wallet} label="Quanto você investiu" value={money(k.spend)} delta={<Delta value={k.deltas.spend} />} vs={vs} hint="Somando Google, Instagram/Facebook e TikTok" />
        <Kpi icon={MessageCircle} label="Clientes que te chamaram" value={num(k.results)} delta={<Delta value={k.deltas.results} />} vs={vs} hint="Conversas, ligações e cadastros" />
        <Kpi icon={Receipt} label="Custo por cliente" value={money2(k.costPerResult)} delta={<Delta value={k.deltas.costPerResult} invert />} vs={vs} hint="Quanto menor, melhor" />
        <Kpi icon={TrendingUp} label="Retorno estimado" value={roasText(k.roas)} delta={<Delta value={k.deltas.roas} />} vs={vs}
          hint={company.avgTicket ? `Com ticket médio de ${money(company.avgTicket)}` : 'Informe o ticket médio nas configurações'} />
      </div>

      <div className="grid g-main">
        {/* o ritmo do orçamento fica sob o gráfico: as duas colunas ficam com altura parecida */}
        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h2>Investimento × clientes</h2>
              <div className="row small muted" style={{ marginLeft: 'auto', gap: 14 }}>
                <span className="row" style={{ gap: 6 }}><span className="status-dot legend-dot spend" /> Investido</span>
                <span className="row" style={{ gap: 6 }}><span className="status-dot legend-dot clients" /> Clientes</span>
              </div>
            </div>
            {k.spend > 0 || k.results > 0
              ? <SpendContactsChart data={toChartData(d.daily)} height={280} />
              : <div className="empty small">{d.syncing ? 'Os dados aparecem aqui assim que a sincronização terminar.' : 'Sem gasto nos últimos 30 dias.'}</div>}
          </div>
          <PacingCard pacing={d.pacing} />
        </div>

        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h3>O que fazer agora</h3>
              <Link to={`/app/${company.id}/sugestoes`} className="link">Ver todas <ArrowRight size={14} /></Link>
            </div>
            <TopSuggestions companyId={company.id} limit={3} />
          </div>
        </div>
      </div>

      <div className="card flush">
        <div className="card-head" style={{ padding: '18px 20px 0' }}>
          <h2>Suas campanhas</h2>
          <Link to={`/app/${company.id}/campanhas`} className="link">Gerenciar <ArrowRight size={14} /></Link>
        </div>
        {d.campaigns.length
          ? <CampaignSummaryTable companyId={company.id} items={d.campaigns} />
          : <div style={{ padding: 20 }}><Empty title={d.syncing ? 'Trazendo suas campanhas…' : 'Nenhuma campanha ainda'}>{d.syncing ? 'Em alguns minutos elas aparecem aqui.' : <Link className="btn primary" to={`/app/${company.id}/criar`}>Criar a primeira campanha</Link>}</Empty></div>}
      </div>
    </div>
  )
}
